import { useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import type {
  AnalyticsDateRange,
  AnalyticsFilters,
} from "@/features/analytics/types";
import type { Coverage } from "@adlywise/shared";
import { createDeviceListRequest } from "../utils";
import { deviceCountOf } from "../coverage";

type UseDeviceCountParams = {
  selectedRange: AnalyticsDateRange;
  appliedFilters: AnalyticsFilters;
};

type UseDeviceCountResult = {
  totalDevices: number | null;
  coverage: Coverage | null;
  isLoading: boolean;
};

export const useDeviceCount = ({
  selectedRange,
  appliedFilters,
}: UseDeviceCountParams): UseDeviceCountResult => {
  const { performRequest } = useApp();
  const [totalDevices, setTotalDevices] = useState<number | null>(null);
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setTotalDevices(null);
    setCoverage(null);

    const base = createDeviceListRequest(selectedRange, appliedFilters, 1, 1);
    const payload = {
      createdAt: base.createdAt,
      filters: base.filters,
      page: 1,
      pageSize: 1,
    };

    setIsLoading(true);
    performRequest<{ totalDevices: number; coverage?: Coverage } | null>(
      "/devices/count",
      {
        method: "POST",
        body: JSON.stringify(payload),
        signal: controller.signal,
      },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        const next = deviceCountOf(response);
        setTotalDevices(next.totalDevices);
        setCoverage(next.coverage);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        // eslint-disable-next-line no-console
        console.error("Failed to load device count", error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [performRequest, selectedRange, appliedFilters]);

  return { totalDevices, coverage, isLoading };
};
